import Error from 'components/Error';
import Loader from 'components/Loader';
import PageHeader from 'components/PageHeader';
import GamingProductList from 'containers/CategoryLandingSlices/GamingProductList';
import StickyLinks from 'containers/ReadyToShip/StickyLinks';
import Prismic from 'helpers/Prismic';
import breakPoints, { colors } from 'helpers/Styles';
import Head from 'next/head';
import { useRouter } from 'next/router';
import Script from 'next/script';
import { useState, useEffect } from 'react';
import { Element } from 'react-scroll';
import globalProps, { globalPropsRequests } from 'utils/globalProps';

const workstations = ({ content, featureListDOCS }) => {
  const ogImage = content?.og_image?.url || null;
  async function getPcModelsByNames(names) {
    try {
      const response = await fetch(
        `/api/search/getAllPcModelsByNames?names=${names.join(',')}`,
        {
          method: 'GET',
          headers: {
            'Content-Type': 'application/json',
          },
        },
      );

      if (!response.ok) {
        throw new Error('Network response was not ok');
      }

      const results = await response.json();
      return results;
    } catch (error) {
      console.error('Error fetching data:', error);
      return null;
    }
  }

  function getUniqueSlugs(urls) {
    const slugs = new Set();

    urls.forEach((url) => {
      const match = url.match(/\/(?:pc-models|series)\/([a-zA-Z0-9-]+)/);
      if (match) {
        if (match[1] === 'vision-mini') {
          slugs.add('visionmini-basic');
        } else if (match[1] === 'focus-workstation') {
          slugs.add('focus-x');
        } else {
          slugs.add(match[1]);
        }
      }
    });

    return Array.from(slugs);
  }

  const { isFallback } = useRouter();

  if (isFallback) {
    return <Loader />;
  }

  if (!content) {
    return <Error code={404} />;
  }

  const {
    seo_title: seoTitle,
    seo_description: seoDescription,
    title,
    description,
    body,
  } = content;
  const [activeNav, setActiveNav] = useState(0);
  const [allPcModels, setAllPcModels] = useState();

  useEffect(() => {
    const pcModelsLinks = [];
    content?.body?.forEach((body) => {
      body.items.forEach((item) => {
        pcModelsLinks.push(item.link);
      });
    });

    const uids = getUniqueSlugs(pcModelsLinks);
    const promises = [];
    const chunkSize = 3;

    for (let i = 0; i < uids.length; i += chunkSize) {
      const chunk = uids.slice(i, i + chunkSize);
      promises.push(getPcModelsByNames(chunk));
    }

    Promise.all(promises).then((responses) => {
      setAllPcModels(responses.flat());
    });
  }, []);

  const listTitles = body.map((bodyItem) => bodyItem.primary.product_title);

  const handleActiveNav = (index) => {
    setActiveNav(index);
  };

  return (
    <>
      <Head>
        <link
          rel='preconnect dns-prefetch'
          href='https://triplewhale-pixel.web.app/'
          crossOrigin='true'
        />
        <link
          rel='preconnect dns-prefetch'
          href='https://api.config-security.com/'
          crossOrigin='true'
        />
        <title>{seoTitle}</title>
        <meta name='description' content={seoDescription} />
        <meta property="og:image" content={ogImage ? ogImage : "https://cdn.shopify.com/s/files/1/0637/0407/2436/files/ogImage.png?v=1714058511"} />
      </Head>
      <Script
        src='/tripleWhale.js'
        dangerouslySetInnerHTML={{
          __html: 'TripleHeadless = "aftershockpcsg.myshopify.com"',
        }}
        strategy='lazyOnload'
      />
      <main>
        {content?.desktop_banner &&
        Object.keys(content.desktop_banner).length !== 0 &&
        content?.mobile_banner &&
        Object.keys(content.mobile_banner).length !== 0 ? (
          <PageHeader
            bannerImage={content.desktop_banner}
            bannerImageMobile={content.mobile_banner}
            title={title}
            description={description[0]?.text}
          />
        ) : null}
        <div className='wrapper'>
          <div className='tabContent'>
            <div className='sticky_list'>
              <ul>
                {listTitles.map((item, listIndex) => (
                  <StickyLinks
                    item={item}
                    listIndex={listIndex}
                    key={item}
                    handleActiveNav={handleActiveNav}
                    activeNav={activeNav}
                  />
                ))}
              </ul>
            </div>
            <div className='gaming_product_list anchor-container'>
              {body.map((bodyItem, index) => (
                <Element
                  name={listTitles[index]}
                  className='element'
                  key={index}
                >
                  <GamingProductList
                    allPcModels={allPcModels}
                    data={bodyItem}
                    features={featureListDOCS}
                    fixedPrice
                    readyToShip={false}
                  />
                </Element>
              ))}
            </div>
          </div>
        </div>
      </main>
      <style jsx>
        {`
          .tabContent {
            display: flex;
          }
          .sticky_list {
            flex: 1;
          }
          .sticky_list ul {
            position: sticky;
            top: 100px;
            padding-top: 50px;
            list-style-type: none;
            margin-bottom: 0px;
            padding-bottom: 16px;
            padding-left: 0px;
          }

          .gaming_product_list {
            flex: 3;
            display: flex;
            flex-direction: column;
            margin-left: auto;
          }
          button {
            padding: 15px 20px;
            background-color: ${colors.brandSecondary};
            font-family: 'Roboto-Bold';
          }
          .button_arrow {
            padding: 15px 20px;
            background-color: #242424;
          }
          main {
            background-color: ${colors.n500};
          }
          .anchor-container {
            padding-top: 330px;
            margin-top: -330px;
          }
          @media (max-width: ${breakPoints.medium}) {
            .tabContent {
              flex-direction: column;
              justify-content: center;
            }
            .gaming_product_list {
              width: 100%;
            }
            .sticky_list {
              position: sticky;
              top: 0px;
              background-color: black;
              z-index: 1;
            }

            .sticky_list ul {
              white-space: nowrap;
              overflow-x: scroll;
              padding-top: 10px;
              padding-right: 10px;
              display: flex;
            }
          }
          .bottom_info {
            padding: 60px 0 80px 0;
            color: #e4e4e4;
            font-family: 'Roboto-Normal';
            font-size: 16px;
            letter-spacing: 0;
            line-height: 24px;
            text-align: center;
            width: 30%;
            justify-content: center;
            margin: auto;
          }
          @media (max-width: ${breakPoints.medium}) {
            .bottom_info {
              width: 100%;
            }
          }
          @media (max-width: 425px) {
            .sticky_list::after {
              content: '';
              width: 60px;
              background: linear-gradient(
                to left,
                rgba(0, 0, 0, 1),
                rgba(0, 0, 0, 0.2)
              );
              height: 100%;
              position: absolute;
              right: 0;
              top: 0;
            }
          }
        `}
      </style>
    </>
  );
};

export async function getStaticProps({ params, preview = false, previewData }) {
  const { handle } = params;
  const { ref } = previewData || {};
  const {
    props: { global },
  } = await globalProps();

  // let menu = null;
  const contentOptions = {
    fetchLinks: ['pc_model.base_price', 'pc_model.base_price_compare_at'],
  };
  if (ref) contentOptions.ref = ref;

  const content = await Prismic.getByUID(
    'workstations_pag',
    `ws-${handle}`,
    contentOptions,
  ).then((res) => res?.data || null);

  if (!content) {
    return { notFound: true };
  }

  globalPropsRequests.push(
    Prismic.getByUID('page_menu', 'menu_ws', ref ? { ref } : null)
      .then((res) => ['menu_ws', res])
      .catch(() => null),
  );
  globalPropsRequests.push(
    Prismic.getByUID(
      'main_navigation_new_version2',
      'main_navigation_new_version2',
      ref ? { ref } : null,
    )
      .then((res) => ['header', res])
      .catch(() => null),
  );
  globalPropsRequests.push(
    Prismic.getByUID('footer', 'footer', ref ? { ref } : null)
      .then((res) => ['footer', res])
      .catch(() => null),
  );

  await Promise.all(globalPropsRequests)
    .then((responses) => {
      responses.forEach((res) => {
        if (res) {
          const [id, body] = res;
          if (id === 'menu_ws') menu = body.data;
          else global[id] = body.data;
        }
      });
    })
    .catch(() => null);

  let featureListDOCS = [];

  if (content?.body) {
    const featureListIDs = content.body
      .map((productGroups) => productGroups.items)
      .flat()
      .map((item) => item?.features?.id)
      .filter((item, i, ar) => ar.indexOf(item) === i && item);
    //.map((item) => item?.features);

    featureListDOCS = await Prismic.getByIDs(
      featureListIDs,
      ref ? { ref } : null,
    ).then((res) => res?.results || []);
  }
  return {
    props: {
      global,
      handle,
      content,
      featureListDOCS,
      // menu,
      preview,
    },
    revalidate: 300,
  };
}

export async function getStaticPaths() {
  return {
    paths: [
      { params: { handle: 'desktops' } },
      { params: { handle: 'laptops' } },
      { params: { handle: 'workstation' } },
    ],
    fallback: true,
  };
}

export default workstations;
